// /graph — the drawing on the left, the selected node's content on the right.
//
// Clicking a node selects it rather than navigating: the graph is the context
// you read the node in, and routing away and back would lose it. The selection
// lives in the query string (?node=slug) so it survives a reload, is linkable,
// and puts the browser Back button on the trail of nodes you looked at.
//
// Below the two panes, every node as a table — id, title, labels and the two
// timestamps — paged by the API so the page stays the same size as the graph
// grows. It is also the view that works on a phone, where the drawing has to be
// scrolled.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageShell from "../components/PageShell";
import GraphFlow from "../components/GraphFlow";
import NodePanel from "../components/NodePanel";
import NodeTable from "../components/NodeTable";
import GraphNav from "../components/GraphNav";
import ReadingProgress from "../components/ReadingProgress";
import graphApi from "../services/graphApi";

// How long to sit on a node after its narration ends, before Next is pressed.
// Long enough to register that the clip finished and to reach for the button if
// you want to stay; short enough that the loop still feels like one session.
const AUTOPLAY_PAUSE_MS = 3000;

// How fast a narration plays, as a multiple of the rate it was synthesised at.
//
// 1.0 means "play it as recorded". The clips come out of the generator at about
// 188 words a minute with no `speed` argument, and slowing them here was a
// second, hidden rate on top of that: the page played every node at 0.8, which
// read as ~150 wpm and made each clip a quarter longer than its own stated
// duration. One rate is easier to reason about than two, and the pace belongs
// in the narration script if it needs changing.
//
// Kept as a named constant rather than deleted: this is the one place to change
// if a slower pace is ever wanted again.
const PLAYBACK_RATE = 1.0;

/** Hold the element at PLAYBACK_RATE.
 *
 * Called from more than one place on purpose: the rate is a property of the
 * ELEMENT, not of the source, and a browser is free to reset it when the src
 * changes or the media reloads. Setting it once at mount would silently revert
 * to 1x on the second node of an autoplay run.
 */
const holdRate = (el) => {
  if (el && el.playbackRate !== PLAYBACK_RATE) el.playbackRate = PLAYBACK_RATE;
};

export default function GraphPage() {
  const [params, setParams] = useSearchParams();
  const selected = params.get("node");
  // The subject being read. In the query string beside the node, so a filtered
  // view is linkable and survives a reload — and so Next, the drawing and the
  // table can never disagree about which subject is on.
  const label = params.get("label");

  const [labels, setLabels] = useState([]);

  const [view, setView] = useState(null);
  const [health, setHealth] = useState(null);
  const [status, setStatus] = useState("loading");
  const [node, setNode] = useState(null);
  const [nodeStatus, setNodeStatus] = useState("idle"); // idle | loading | ready | error
  // Bumped each time a node is opened, so the table's "Last opened" column
  // reflects the visit that just happened rather than the one before it.
  const [opened, setOpened] = useState(0);

  // The reading path lives in the database (graph_visits), not here. The page
  // only mirrors whether Prev has anywhere to go; the server replays the
  // back-stack from the visit log, so a reload keeps the path.
  const [canGoBack, setCanGoBack] = useState(false);
  // How the NEXT selection was reached, read by the fetch that records it.
  const viaRef = useRef({ via: "click", mode: null });
  const [nav, setNav] = useState({ busy: false, why: "", mode: null });

  // Autoplay: read this node aloud, wait, press Next, repeat. The loop lives
  // here rather than in the panel because only the page can press Next — the
  // panel knows one node, and the clip ending is the only thing it can report.
  const [autoplay, setAutoplay] = useState(false);
  const [playing, setPlaying] = useState(false);
  // ONE audio element for the whole page, never unmounted. A browser grants
  // permission to play to an element, off the gesture that asked for it — so a
  // player that is torn down with each node has to win that permission again
  // every time, and loses. This one keeps the permission Autoplay gave it.
  const audioRef = useRef(null);
  const autoTimer = useRef(null);
  // The loop calls Next from a timer, so it needs the CURRENT goNext, not the
  // one captured when the timer was set.
  const goNextRef = useRef(null);
  // One advance per node. Without it a re-render while the timer is pending
  // would queue a second Next and skip a node.
  const advancedFor = useRef(null);

  // A node the drawing has never heard of means the drawing is out of date —
  // the graph gained nodes (a sync) while this page was open. Refetching the
  // whole view is the only repair, and the missing node is a precise trigger
  // for it: no polling, and one request exactly when it is needed.
  const restaleGuard = useRef(null);
  // The graph fingerprint last drawn. `/path` is fetched after every visit
  // anyway, so it costs nothing to carry the revision along and refetch the
  // drawing only when it actually moves — a retitle or an edge change leaves
  // the node set identical, which is why "is the selected node known?" was not
  // enough on its own.
  const revisionRef = useRef(null);
  // The current subject, for the refetches that fire from callbacks rather than
  // from render — a stale closure there would quietly redraw the whole graph.
  const labelRef = useRef(label);
  labelRef.current = label;

  const refreshPath = useCallback(async () => {
    try {
      const state = await graphApi.path();
      setCanGoBack(state.can_go_back);
      if (state.graph_revision && revisionRef.current !== state.graph_revision) {
        const stale = revisionRef.current !== null;
        revisionRef.current = state.graph_revision;
        if (stale) graphApi.getOrder(labelRef.current).then(setView).catch(() => {});
      }
    } catch {
      setCanGoBack(false);
    }
  }, []);

  // Redrawn whenever the subject changes: the drawing IS the filter's main
  // effect, so it cannot wait for the next visit to catch up.
  useEffect(() => {
    graphApi
      .getOrder(label)
      .then((data) => {
        setView(data);
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
  }, [label]);

  useEffect(() => {
    // Health is a nice-to-have: a failure here must not blank the page.
    graphApi.getHealth().then(setHealth).catch(() => {});
    graphApi.getLabels().then(setLabels).catch(() => setLabels([]));
  }, []);

  useEffect(() => {
    if (!selected) {
      setNode(null);
      setNodeStatus("idle");
      return;
    }
    let current = true;
    setNodeStatus("loading");
    const { via, mode } = viaRef.current;
    viaRef.current = { via: "click", mode: null }; // the default for a bare visit
    graphApi
      .getNode(selected, via, mode)
      .then((data) => {
        // A slower earlier request must not overwrite a newer selection.
        if (!current) return;
        setNode(data);
        setNodeStatus("ready");
        setOpened((n) => n + 1);
        refreshPath();
        // Guarded by slug so a node genuinely absent from the graph cannot
        // send this into a refetch loop.
        if (restaleGuard.current !== data.slug) {
          restaleGuard.current = data.slug;
          setView((v) => {
            if (v && !v.layers.some((l) => l.nodes.some((n) => n.slug === data.slug))) {
              graphApi.getOrder(labelRef.current).then(setView).catch(() => {});
            }
            return v;
          });
        }
      })
      .catch(() => current && setNodeStatus("error"));
    return () => {
      current = false;
    };
  }, [selected, refreshPath]);

  // replace, not push, when swapping between nodes: Back should leave the graph
  // rather than walk every node touched on the way here. Prev is the trail
  // instead (§2.1).
  const select = useCallback(
    (slug, options = {}) => {
      // The step is recorded server-side when the node is fetched; `via` is
      // stashed here so that fetch knows how the reader arrived.
      viaRef.current = { via: options.via || "click", mode: options.mode || null };
      // The subject rides along: dropping it here would silently widen the
      // filter every time a node was opened.
      const next = {};
      if (slug) next.node = slug;
      if (label) next.label = label;
      setParams(next, { replace: Boolean(selected) });
    },
    [setParams, selected, label],
  );

  // Switching subject keeps the node you are reading only if it belongs to the
  // new subject — otherwise the pane would show something the drawing beside it
  // no longer contains.
  const pickLabel = useCallback(
    (next) => {
      const keep = !next || (node?.labels || []).includes(next);
      const query = {};
      if (keep && selected) query.node = selected;
      if (next) query.label = next;
      setParams(query);
    },
    [setParams, selected, node],
  );

  // §2.2 — the home of /graph is a fresh start, so the path restarts there.
  useEffect(() => {
    if (selected) return;
    setNav({ busy: false, why: "", mode: null });
    graphApi
      .resetPath()
      .then(() => setCanGoBack(false))
      .catch(() => setCanGoBack(false));
  }, [selected]);

  // Prev asks the server where the path came from. Opening that node with
  // via="prev" is what pops the back-stack — it does not push the node being
  // left, or Prev and Next-after-Prev would bounce forever (§2.3).
  const goPrev = async () => {
    setNav((n) => ({ ...n, busy: true }));
    try {
      const result = await graphApi.prev();
      if (result.node) {
        setNav({ busy: false, why: "", mode: null });
        select(result.node.slug, { via: "prev" });
        return;
      }
      // Nothing earlier in the path, but a node is open — so the step back is
      // to the graph itself. The path STARTS at the bare /graph; treating that
      // as "no previous step" left the first node a dead end, with Prev greyed
      // out and no way back short of the heading.
      if (selected) {
        setNav({ busy: false, why: "", mode: null });
        select(null);
        return;
      }
      setNav({ busy: false, why: result.why, mode: null });
    } catch {
      setNav({ busy: false, why: "could not read the path", mode: null });
    }
  };

  const clearAutoTimer = () => {
    if (autoTimer.current) {
      clearTimeout(autoTimer.current);
      autoTimer.current = null;
    }
  };

  // Every advance in the loop goes through here, so the pause is one number in
  // one place and the guard cannot be bypassed.
  const queueAdvance = useCallback((slug) => {
    if (advancedFor.current === slug) return;
    advancedFor.current = slug;
    clearAutoTimer();
    autoTimer.current = setTimeout(() => {
      autoTimer.current = null;
      if (goNextRef.current) goNextRef.current();
    }, AUTOPLAY_PAUSE_MS);
  }, []);

  const toggleAutoplay = () => {
    if (autoplay) {
      clearAutoTimer();
      setAutoplay(false);
      if (audioRef.current) audioRef.current.pause();
      return;
    }
    advancedFor.current = null;
    setAutoplay(true);
    // Start playback INSIDE the click, while the gesture is still live. That is
    // what the element needs to be allowed to play unprompted later; with no
    // node open there is nothing to play yet, so prime it with a load() — which
    // the same gesture also authorises.
    const audio = audioRef.current;
    if (audio) {
      holdRate(audio);
      if (node?.audio) audio.play().catch(() => {});
      else audio.load();
    }
    // Nothing open yet, so there is no clip to wait for — start the loop by
    // asking what to read.
    if (!selected) goNext();
  };

  // The Listen pill, for a node being read by hand.
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    holdRate(audio);
    if (audio.paused) audio.play().catch(() => setPlaying(false));
    else audio.pause();
  };

  // Under autoplay, a node that has just opened is a clip to start. A node with
  // no narration cannot report that it finished, so the page waits the same
  // beat and moves on — without that the loop stops dead on the first unvoiced
  // node.
  useEffect(() => {
    if (!autoplay || nodeStatus !== "ready" || !node) return;
    if (!node.audio) {
      queueAdvance(node.slug);
      return;
    }
    const audio = audioRef.current;
    if (!audio) return;
    holdRate(audio);
    audio.play().catch(() => {
      // The browser refused. Say so rather than leaving a loop that looks on
      // and is silent.
      clearAutoTimer();
      setAutoplay(false);
      setNav((n) => ({
        ...n,
        why: "the browser blocked playback — press Listen once, then Autoplay",
      }));
    });
  }, [autoplay, nodeStatus, node, queueAdvance]);

  // Leaving the page must not leave a timer that navigates later.
  useEffect(() => () => clearAutoTimer(), []);

  const goNext = async () => {
    setNav((n) => ({ ...n, busy: true }));
    try {
      const result = await graphApi.next(label);
      if (result.node) {
        setNav({ busy: false, why: result.why, mode: result.mode });
        select(result.node.slug, { via: "next", mode: result.mode });
      } else {
        // An empty answer is now a normal end to a session rather than a
        // near-impossible edge: the recommender no longer invents a node when
        // nothing is due and nothing new is ready. Autoplay has to be switched
        // off with it, or the button sits lit over a loop that has stopped.
        clearAutoTimer();
        setAutoplay(false);
        setNav({ busy: false, why: result.why || "nothing to recommend", mode: null });
      }
    } catch {
      setNav({ busy: false, why: "could not reach the recommender", mode: null });
    }
  };

  goNextRef.current = goNext;

  const known = view
    ? new Map(view.layers.flatMap((l) => l.nodes).map((n) => [n.slug, n.title]))
    : null;
  const problems = health
    ? health.dangling.length +
      health.rejected.length +
      health.unreadable.length +
      (health.duplicates?.length || 0)
    : 0;

  // Built once and handed to whichever half of the pane is showing, so the
  // controls are the same block whether or not a node is open.
  const navControls = (
    <GraphNav
      // The graph itself counts as a step back, so Prev is live whenever a node
      // is open — never a dead end.
      canGoBack={canGoBack || Boolean(selected)}
      busy={nav.busy}
      reason={nav.why}
      mode={nav.mode}
      onPrev={goPrev}
      onNext={goNext}
      className=""
      autoplay={autoplay}
      onToggleAutoplay={toggleAutoplay}
    />
  );

  return (
    <PageShell wide>
      {/* The page's one player. Rendered unconditionally and never keyed, so it
          survives every node change — see `audioRef`. `src` is swapped as the
          node changes, which does not cost it its permission to play. */}
      <audio
        ref={audioRef}
        src={node?.audio || undefined}
        preload="none"
        onLoadedMetadata={(e) => holdRate(e.currentTarget)}
        onPlay={(e) => {
          holdRate(e.currentTarget);
          setPlaying(true);
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          if (autoplay && node) queueAdvance(node.slug);
        }}
      />
      <header className="mb-6">
        {/* The title is the way back to the whole graph. Linking to "/graph"
            drops the ?node=, which also resets the trail (§2.2) — landing on
            the bare graph is starting again, and the heading is where a reader
            already expects "take me back to the top". */}
        <Link
          to="/graph"
          className="text-3xl font-bold tracking-tight text-slate-100 transition hover:text-sky-300"
        >
          Knowledge graph
        </Link>
      </header>

      {status === "loading" && <p className="text-slate-500">Loading the graph…</p>}
      {status === "error" && (
        <p className="text-rose-400">Could not load the graph. Is the backend running?</p>
      )}

      {status === "ready" && view && view.node_count === 0 && (
        <p className="text-slate-500">
          No nodes yet. Add a markdown file to <code>knowledge_graph/</code> and
          re-sync.
        </p>
      )}

      {status === "ready" && view && view.node_count > 0 && (
        <>
          {/* The subject selector and the counts read as one line: pick a
              subject on the left, and the tallies beside it are that subject's,
              because the drawing, the table and Next all narrow with it. */}
          <div className="mb-4 flex flex-wrap items-center gap-3 text-xs text-slate-400">
            {labels.length > 0 && (
              <label className="flex items-center gap-2">
                <span className="text-slate-500">Subject</span>
                <select
                  value={label || ""}
                  onChange={(e) => pickLabel(e.target.value || null)}
                  className="rounded-lg border border-slate-800 bg-slate-900/60 px-2 py-1 text-slate-200 outline-none transition focus:border-sky-500/40"
                >
                  <option value="">All subjects</option>
                  {labels.map((item) => (
                    <option key={item.label} value={item.label}>
                      {item.label} ({item.count})
                    </option>
                  ))}
                </select>
              </label>
            )}
            {/* The total first. Like every tally here it is the SUBJECT'S
                total, not the graph's — node_count is the filtered view's own
                count. Every node is a concept, so there is no split to show. */}
            <span className="rounded-full bg-slate-800 px-2.5 py-1 text-slate-200">
              {view.node_count} nodes
            </span>
            <span className="rounded-full bg-slate-800 px-2.5 py-1">
              {view.edge_count} edges
            </span>
            <span className="rounded-full bg-slate-800 px-2.5 py-1">
              {view.layers.length} layers
            </span>
            {problems > 0 && (
              <span className="rounded-full bg-amber-500/15 px-2.5 py-1 text-amber-400">
                {problems} to fix
              </span>
            )}
            {/* Boot skips the embedding pass, so an empty duplicates list means
                "not checked" rather than "nothing found". Say which. */}
            {health?.embeddings_skipped && (
              <span
                className="rounded-full bg-slate-800 px-2.5 py-1 text-slate-500"
                title="The duplicate check runs on an admin sync, not at startup"
              >
                duplicates not checked
              </span>
            )}
          </div>

          {/* One column until lg, where there is room for two panes side by
              side.

              Below lg the drawing is DROPPED, not stacked. It is a field of
              drifting titles that needs room to be read at all; on a phone it
              is a dark box between the reader and the node, and the same graph
              is reachable through the node's own links and the table. */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <div className="hidden lg:col-span-6 lg:block">
              <div className="lg:sticky lg:top-6">
                <GraphFlow
                  layers={view.layers}
                  edges={view.edges}
                  activeSlug={selected}
                  onSelect={select}
                />
              </div>
            </div>

            <aside className="lg:col-span-6">
              <div className="rounded-xl border border-slate-800 bg-slate-900/30 p-5 lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto">
                {nodeStatus === "idle" && (
                  <div className="py-8 text-center">
                    <p className="text-sm text-slate-400">
                      Click a node to read it here.
                    </p>
                    <p className="mt-2 text-xs text-slate-500">
                      The drawing stays put, so you keep the context the node
                      sits in.
                    </p>
                  </div>
                )}
                {nodeStatus === "loading" && (
                  <p className="py-8 text-center text-sm text-slate-500">Loading…</p>
                )}
                {nodeStatus === "error" && (
                  <p className="py-8 text-center text-sm text-rose-400">
                    Could not load that node.
                  </p>
                )}
                {nodeStatus === "ready" && node && (
                  <NodePanel
                    node={node}
                    known={known}
                    onSelect={select}
                    compact
                    nav={navControls}
                    playing={playing}
                    onTogglePlay={togglePlay}
                    // A deleted node has left the graph, so the drawing and the
                    // table must be refetched — the node it was showing is gone.
                    onDeleted={() => {
                      graphApi.getOrder(labelRef.current).then(setView).catch(() => {});
                      setOpened((n) => n + 1);
                    }}
                  />
                )}
                {/* Before a node is open the navigation still has to be
                    reachable — Next is how a reading session starts. */}
                {nodeStatus !== "ready" && (
                  <div className="flex justify-center">{navControls}</div>
                )}
              </div>
            </aside>
          </div>

          {/* Under the reading pane, above the table: it is the record of what
              the Next button has been doing, so it belongs with the reading,
              not with the inventory. `opened` bumps on every node read, which
              is exactly when a curve can have moved. */}
          <div className="mt-12">
            <ReadingProgress refreshKey={opened} />
          </div>

          <div className="mt-12">
            <NodeTable selected={selected} onSelect={select} refreshKey={opened} label={label} />
          </div>

          {health && problems > 0 && (
            <section className="mt-10 rounded-xl border border-amber-500/30 bg-amber-500/5 p-5">
              <h2 className="mb-3 text-lg font-semibold text-amber-300">Needs fixing</h2>
              <ul className="space-y-2 text-sm text-slate-300">
                {(health.duplicates || []).map((item) => (
                  <li key={`dup-${item.node}`}>
                    <code className="text-slate-200">{item.node}</code> may duplicate{" "}
                    <code className="text-slate-200">{item.nearest}</code> — cosine{" "}
                    {Number(item.similarity).toFixed(4)}, {item.z}σ above its other
                    neighbours. Merge them, or sharpen whichever one is vaguer.
                  </li>
                ))}
                {health.unreadable.map((item) => (
                  <li key={`u-${item.node}`}>
                    <code className="text-slate-200">{item.node}.md</code> could not
                    be read — {item.detail}
                  </li>
                ))}
                {health.rejected.map((item) => (
                  <li key={`r-${item.source}-${item.target}`}>
                    <code className="text-slate-200">
                      {item.source} → {item.target}
                    </code>{" "}
                    rejected ({item.kind}) — {item.detail}
                  </li>
                ))}
                {health.dangling.map((item) => (
                  <li key={`d-${item.node}-${item.missing}`}>
                    <code className="text-slate-200">{item.node}</code> links{" "}
                    <code className="text-slate-200">[[{item.missing}]]</code>, which
                    has no file yet
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </PageShell>
  );
}
